<script >
import ChartingSidebarPanelAlerts from '@/features/charting-next/components/workspace/sidebar/ChartingSidebarPanelAlerts.vue';
import ChartingSidebarPanelAnalysisGroup from '@/features/charting-next/components/workspace/sidebar/ChartingSidebarPanelAnalysisGroup.vue';
import ChartingSidebarPanelDescription from '@/features/charting-next/components/workspace/sidebar/ChartingSidebarPanelDescription.vue';
import ChartingSidebarPanelError from '@/features/charting-next/components/workspace/sidebar/ChartingSidebarPanelError.vue';
import { useChartingSidebarState } from '@/features/charting-next/composables/useChartingSidebarState';
import { createChartingSidebarPanelItems } from '@/features/charting-next/models/chartingSidebarPanels';
import { computed, toRef } from '@vue/composition-api';
import { useChartingToast as useToast } from '@/features/charting-next/services/chartingFeedback';
const __sfc_main = {};
__sfc_main.props = {
  stock: {
    type: Object,
    default: null
  },
  activePanel: {
    type: String,
    default: ''
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const toast = useToast();
  const sidebar = useChartingSidebarState(toRef(props, 'stock'));
  const panelItems = computed(() => createChartingSidebarPanelItems({
    capabilities: sidebar.capabilities.value
  }));
  const platinumScoreLabel = computed(() => sidebar.selectedStock.value?.platinumScoreLabel || 'N/D');
  const hasPlatinumScore = computed(() => Boolean(sidebar.selectedStock.value?.platinumScoreLabel));
  const notify = ({
    severity = 'info',
    summary = '',
    detail = '',
    life = 3000
  } = {}) => {
    toast.add({
      severity,
      summary,
      detail: detail || summary,
      life
    });
  };
  const togglePanel = item => {
    if (item.disabled) {
      notify({
        severity: 'warn',
        summary: item.title,
        detail: item.reason || 'Acción no disponible con el acceso actual.'
      });
      return;
    }
    emit('update:active-panel', props.activePanel === item.key ? '' : item.key);
  };
  const notifyResult = (result, fallbackTitle) => {
    notify({
      severity: 'success',
      summary: fallbackTitle,
      detail: result?.message || fallbackTitle
    });
  };
  const createAlert = async () => {
    if (!sidebar.alertDraft.value.value) {
      notify({
        severity: 'warn',
        summary: 'Alarmas',
        detail: 'Configura un valor para la alarma.'
      });
      return;
    }
    try {
      const result = await sidebar.createAlert();
      notifyResult(result, 'Alarma creada');
    } catch (error) {
      notify({
        severity: 'error',
        summary: 'Alarmas',
        detail: error.message || 'No se pudo crear la alarma.'
      });
    }
  };
  const removeAlert = async alertId => {
    try {
      const result = await sidebar.removeAlert(alertId);
      notifyResult(result, 'Alarma eliminada');
    } catch (error) {
      notify({
        severity: 'error',
        summary: 'Alarmas',
        detail: error.message || 'No se pudo eliminar la alarma.'
      });
    }
  };
  const submitErrorReport = async () => {
    if (!sidebar.errorDraft.value.message.trim()) {
      notify({
        severity: 'warn',
        summary: 'Reportar error',
        detail: 'Describe la incidencia antes de enviarla.'
      });
      return;
    }
    try {
      const result = await sidebar.submitErrorReport();
      notifyResult(result, 'Incidencia enviada');
    } catch (error) {
      notify({
        severity: 'error',
        summary: 'Reportar error',
        detail: error.message || 'No se pudo enviar la incidencia.'
      });
    }
  };
  return {
    sidebar,
    panelItems,
    platinumScoreLabel,
    hasPlatinumScore,
    togglePanel,
    createAlert,
    removeAlert,
    submitErrorReport
  };
};
__sfc_main.components = Object.assign({
  ChartingSidebarPanelAnalysisGroup,
  ChartingSidebarPanelDescription,
  ChartingSidebarPanelAlerts,
  ChartingSidebarPanelError
}, __sfc_main.components);
export default __sfc_main;
</script>

<template>
    <aside class="charting-workspace-inspector" :class="{ 'is-expanded': activePanel }">
        <div class="charting-workspace-inspector__rail">
            <div class="charting-workspace-inspector__platinum-score" :class="{ 'is-empty': !hasPlatinumScore }" title="Puntuacion Platino" aria-label="Puntuacion Platino">
                <span class="charting-workspace-inspector__platinum-value">{{ platinumScoreLabel }}</span>
                <span class="charting-workspace-inspector__platinum-label">Puntos</span>
            </div>

            <button
                v-for="item in panelItems"
                :key="item.key"
                type="button"
                class="charting-workspace-inspector__item"
                :class="{ 'is-active': activePanel === item.key, 'is-disabled': item.disabled }"
                :title="item.reason || item.title"
                :aria-label="item.title"
                @click="togglePanel(item)"
            >
                <i :class="item.icon"></i>
                <span>{{ item.label }}</span>
            </button>
        </div>

        <div v-if="activePanel" class="charting-workspace-inspector__panel">
            <ChartingSidebarPanelAnalysisGroup
                v-if="activePanel === 'analysis'"
                :stock="sidebar.selectedStock.value"
                :fundamental-resource="sidebar.fundamentalResource"
                :has-selected-ticker="Boolean(sidebar.selectedTicker.value)"
                :can-view-basic-insights="sidebar.capabilities.value.canViewBasicInsights"
                :can-use-fundamental="sidebar.capabilities.value.canUseFundamental"
            />
            <ChartingSidebarPanelDescription v-else-if="activePanel === 'description'" :stock="sidebar.selectedStock.value" :resource="sidebar.fundamentalResource" />
            <ChartingSidebarPanelAlerts
                v-else-if="activePanel === 'alarms'"
                :alert-draft="sidebar.alertDraft.value"
                :alerts-resource="sidebar.alertsResource"
                :pending-alerts="sidebar.pendingAlerts.value"
                :executed-alerts="sidebar.executedAlerts.value"
                :is-submitting-alert="sidebar.isSubmittingAlert.value"
                @update:alert-draft="sidebar.alertDraft.value = $event"
                @select-alert-field="sidebar.selectAlertField"
                @create-alert="createAlert"
                @remove-alert="removeAlert"
            />
            <ChartingSidebarPanelError
                v-else-if="activePanel === 'error'"
                :error-draft="sidebar.errorDraft.value"
                :is-submitting-error="sidebar.isSubmittingError.value"
                @update:error-draft="sidebar.errorDraft.value = $event"
                @submit-error-report="submitErrorReport"
            />
        </div>
    </aside>
</template>

<style scoped>
.charting-workspace-inspector {
    display: flex;
    min-height: 0;
    height: 100%;
    flex-direction: row-reverse;
    align-items: stretch;
    gap: 0.75rem;
}

.charting-workspace-inspector__rail {
    display: flex;
    width: 4.75rem;
    min-width: 4.75rem;
    flex-direction: column;
    align-items: center;
    gap: 0.9rem;
    padding: 0.15rem 0;
    overflow-y: auto;
}

.charting-workspace-inspector__item {
    display: flex;
    width: 4.55rem;
    min-width: 4.55rem;
    height: 4.55rem;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.42rem;
    border: 1px solid var(--brand-panel-border);
    border-radius: 1.25rem;
    background: var(--charting-b-panel-bg-strong, rgba(255, 255, 255, 0.92));
    background-clip: padding-box;
    color: var(--p-text-color);
    cursor: pointer;
    transition:
        border-color 0.18s ease,
        background-color 0.18s ease,
        box-shadow 0.18s ease;
}

.charting-workspace-inspector__item i {
    font-size: 1.38rem;
}

.charting-workspace-inspector__item span {
    max-width: 3.45rem;
    overflow: hidden;
    font-size: 0.61rem;
    font-weight: 700;
    line-height: 1;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.charting-workspace-inspector__item:hover,
.charting-workspace-inspector__item.is-active {
    border-color: color-mix(in srgb, var(--brand-primary), transparent 38%);
    background: var(--charting-b-hover-bg, rgba(224, 242, 254, 0.68));
    box-shadow: 0 14px 30px rgba(14, 165, 233, 0.12);
}

.charting-workspace-inspector__item.is-disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

.charting-workspace-inspector__platinum-score {
    display: flex;
    width: 4.55rem;
    min-width: 4.55rem;
    min-height: 4.55rem;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.24rem;
    border: 1px solid color-mix(in srgb, var(--brand-primary), transparent 42%);
    border-radius: 1.25rem;
    background: var(--charting-b-panel-bg-strong, rgba(255, 255, 255, 0.92));
    color: var(--p-text-color);
    text-align: center;
}

.charting-workspace-inspector__platinum-score.is-empty {
    border-color: var(--brand-panel-border);
    color: var(--p-text-muted-color);
}

.charting-workspace-inspector__platinum-value {
    max-width: 3.45rem;
    overflow: hidden;
    font-size: 1.45rem;
    font-weight: 800;
    line-height: 1;
}

.charting-workspace-inspector__platinum-label {
    max-width: 3.45rem;
    overflow: hidden;
    font-size: 0.6rem;
    font-weight: 700;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.charting-workspace-inspector__panel {
    display: flex;
    min-width: 0;
    min-height: 0;
    flex: 1 1 auto;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid var(--brand-panel-border);
    border-radius: 1.5rem;
    background: var(--brand-panel-bg);
    background-clip: padding-box;
    box-shadow: var(--brand-panel-shadow);
    padding: 1rem;
}

::v-deep .charting-workspace-inspector__panel-body {
    display: flex;
    min-height: 0;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 1rem;
    margin: -0.15rem;
    overflow-y: auto;
    padding: 0.15rem 0.35rem 0.15rem 0.15rem;
}

::v-deep .charting-workspace-inspector__summary-grid {
    display: grid;
    gap: 0.75rem;
}

::v-deep .charting-workspace-inspector__summary-cell {
    border: 1px solid var(--charting-b-border-color, rgba(148, 163, 184, 0.18));
    border-radius: 1rem;
    padding: 0.85rem 1rem;
    background: var(--charting-b-panel-bg-strong, rgba(255, 255, 255, 0.92));
}

::v-deep .charting-workspace-inspector__summary-label {
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--p-text-muted-color);
}

::v-deep .charting-workspace-inspector__summary-value {
    margin-top: 0.4rem;
    font-weight: 600;
    color: var(--p-text-color);
}

::v-deep .charting-workspace-inspector__loader {
    display: flex;
    justify-content: center;
}

::v-deep .charting-workspace-inspector__event {
    border: 1px solid var(--charting-b-border-color, rgba(148, 163, 184, 0.18));
    border-radius: 1rem;
    background: var(--charting-b-panel-bg-strong, rgba(255, 255, 255, 0.92));
    padding: 0.85rem 1rem;
}

::v-deep .charting-workspace-inspector__select {
    width: 100%;
    border: 1px solid var(--charting-b-border-color, rgba(148, 163, 184, 0.22));
    border-radius: 0.9rem;
    padding: 0.75rem 0.9rem;
    background: var(--charting-b-panel-bg-strong, rgba(255, 255, 255, 0.95));
    color: var(--p-text-color);
}

::v-deep .charting-workspace-inspector__link {
    display: inline-block;
    margin-top: 0.4rem;
    word-break: break-word;
    color: var(--p-primary-color);
}

@media (max-width: 1099px) {
    .charting-workspace-inspector {
        height: auto;
        flex-direction: column;
    }

    .charting-workspace-inspector__rail {
        width: 100%;
        min-width: 0;
        flex-direction: row;
        justify-content: flex-start;
        padding: 0.55rem;
    }

    .charting-workspace-inspector__panel {
        min-height: 18rem;
    }
}
</style>
